import './otp-form.scss';

import { Controller, type UseFormReturn, useWatch } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import type { OtpFormValues } from '../../types/otp-form';

const fields: (keyof OtpFormValues)[] = ['first', 'second', 'third', 'fourth'];

interface IProps {
  resetOtpError: () => void;
  methods: UseFormReturn<OtpFormValues, unknown, OtpFormValues>;
  onSubmit: (values: OtpFormValues) => void;
}

const OtpForm: React.FC<IProps> = ({ resetOtpError, methods, onSubmit }) => {
  const { t } = useTranslation();
  const {
    control,
    formState: { errors },
    trigger
  } = methods;
  const values = useWatch({ control, name: fields });

  const handleChange = (value: string, onChange: (value: string) => void) => {
    resetOtpError();
    if (value.match(/\D/) || value.length > 1) return false;
    onChange(value);
    const isValid = values.filter(Boolean).length >= 3;
    if (isValid) methods.handleSubmit(onSubmit)();
    return true;
  };

  const handlePaste = (
    e: React.ClipboardEvent<HTMLInputElement>,
    field: keyof OtpFormValues
  ) => {
    const value = e.clipboardData.getData('Text');
    if (value.length === 1) methods.setValue(field, value);
    if (value.length === 4) {
      for (let i = 0; i < fields.length; i++) {
        methods.setValue(fields[i], value[i]);
      }
      methods.handleSubmit(onSubmit)();
    }
  };

  return (
    <div className="otp-form">
      <Controller
        control={control}
        name="first"
        rules={{ required: true, minLength: 1 }}
        render={({ field }) => (
          <input
            {...field}
            type="tel"
            aria-label={t('a11y.otp-digit', { index: 1, total: 4 })}
            value={field.value ?? ''}
            onChange={(e) => {
              const value = e.target.value;
              const shouldContinue = handleChange(value, field.onChange);
              if (value.length === 1 && shouldContinue)
                methods.setFocus('second');
            }}
            className={`otp-form__field ym-disable-keys ${
              errors.first ? 'otp-form__field--error' : ''
            }`}
            autoComplete="one-time-code"
            onPaste={(e) => handlePaste(e, 'first')}
          />
        )}
      />
      <Controller
        control={methods.control}
        name="second"
        rules={{ required: true, minLength: 1 }}
        render={({ field }) => (
          <input
            {...field}
            type="tel"
            aria-label={t('a11y.otp-digit', { index: 2, total: 4 })}
            value={field.value ?? ''}
            onChange={(e) => {
              const value = e.target.value;
              const shouldContinue = handleChange(value, field.onChange);
              if (value.length === 1 && shouldContinue)
                methods.setFocus('third');
            }}
            onKeyDown={(e) => {
              if (e.key === 'Backspace' && !field.value)
                methods.setFocus('first');
            }}
            className={`otp-form__field ym-disable-keys ${
              errors.second ? 'otp-form__field--error' : ''
            }`}
            autoComplete="one-time-code"
            onPaste={(e) => handlePaste(e, 'second')}
          />
        )}
      />
      <Controller
        control={methods.control}
        name="third"
        rules={{ required: true, minLength: 1 }}
        render={({ field }) => (
          <input
            {...field}
            type="tel"
            aria-label={t('a11y.otp-digit', { index: 3, total: 4 })}
            value={field.value ?? ''}
            onChange={(e) => {
              const value = e.target.value;
              const shouldContinue = handleChange(value, field.onChange);
              if (value.length === 1 && shouldContinue)
                methods.setFocus('fourth');
            }}
            onKeyDown={(e) => {
              if (e.key === 'Backspace' && !field.value)
                methods.setFocus('second');
            }}
            className={`otp-form__field ym-disable-keys ${
              errors.third ? 'otp-form__field--error' : ''
            }`}
            autoComplete="one-time-code"
            onPaste={(e) => handlePaste(e, 'third')}
          />
        )}
      />
      <Controller
        control={methods.control}
        name="fourth"
        rules={{ required: true, minLength: 1 }}
        render={({ field }) => (
          <input
            {...field}
            type="tel"
            aria-label={t('a11y.otp-digit', { index: 4, total: 4 })}
            value={field.value ?? ''}
            onChange={(e) => {
              const value = e.target.value;
              const shouldContinue = handleChange(value, field.onChange);
              if (value.length === 1 && shouldContinue)
                trigger(['first', 'second', 'third', 'fourth'], {
                  shouldFocus: true
                });
            }}
            onKeyDown={(e) => {
              if (e.key === 'Backspace' && !field.value)
                methods.setFocus('third');
            }}
            className={`otp-form__field ym-disable-keys ${
              errors.fourth ? 'otp-form__field--error' : ''
            }`}
            autoComplete="one-time-code"
            onPaste={(e) => handlePaste(e, 'fourth')}
          />
        )}
      />
    </div>
  );
};

export default OtpForm;
